import type { PostProcessPopupParams } from "ag-grid-enterprise";
import type { Dispatch, ReactPortal, SetStateAction } from "react";
import type { GridApi } from "~/components/grid/AgGrid";

import Box from "@mui/material/Box";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { RetryButton } from "~/components/Buttons";
import gridFilterFeedback from "~/helpers/grid/gridFilterFeedback";
import { useSnacks } from "~/providers/SnackProvider";

const emptyApi = {};

interface GridFilterFeedback {
	errorPortal: ReactPortal | null;
	postProcessPopup: (params: PostProcessPopupParams) => void;
	setApi: Dispatch<SetStateAction<GridApi | undefined>>;
}

const useGridFilterFeedback = (): GridFilterFeedback => {
	const [api, setApi] = useState<GridApi>();
	const [popup, setPopup] = useState<PostProcessPopupParams>();
	const [openSnack] = useSnacks();
	const { t } = useTranslation();
	const lastRefusal = useRef<unknown>(undefined);
	const subscribe = useCallback(
		(listener: () => void) => gridFilterFeedback.subscribeGridFilterFeedback(api ?? emptyApi, listener),
		[api]
	);
	const getSnapshot = useCallback(() => gridFilterFeedback.getGridFilterFeedback(api ?? emptyApi), [api]);
	const state = useSyncExternalStore(subscribe, getSnapshot);

	useEffect(() => {
		if (!state.refusal || lastRefusal.current === state.refusal) return;
		lastRefusal.current = state.refusal;
		const column = api?.getColumn(state.refusal.colId);
		const columnName = column ? api?.getDisplayNameForColumn(column, "header") : undefined;
		openSnack(
			t(state.refusal.operation === "sort" ? "grid.sortRefused" : "grid.filterRefused", {
				column: columnName || state.refusal.colId
			}),
			"warning"
		);
		if (api) gridFilterFeedback.clearGridQueryRefusal(api);
	}, [api, openSnack, state.refusal, t]);

	const postProcessPopup = useCallback((params: PostProcessPopupParams) => {
		if (params.column && (params.type === "columnFilter" || params.type === "columnMenu")) setPopup(params);
	}, []);
	const retry = popup?.column && popup.api === api ? state.failures.get(popup.column.getColId())?.retry : undefined;
	const errorPortal =
		popup && retry
			? createPortal(
					<Box
						role="alert"
						sx={{
							alignItems: "center",
							bgcolor: "background.paper",
							display: "flex",
							flexDirection: "column",
							gap: 1,
							inset: 0,
							justifyContent: "center",
							p: 2,
							position: "absolute",
							zIndex: 1
						}}
					>
						<Box>{t("grid.filterValuesLoadError")}</Box>
						<RetryButton
							autoFocus
							onClick={(event) => {
								event.stopPropagation();
								retry();
							}}
							variant="outlined"
						/>
					</Box>,
					popup.ePopup
				)
			: null;
	return { errorPortal, postProcessPopup, setApi };
};

export default useGridFilterFeedback;
